import * as React from 'react';
import { Outlet } from 'react-router-dom';

import 'font-awesome/css/font-awesome.min.css';
import 'css-file-icons/build/css-file-icons.css';
import 'regenerator-runtime/runtime';

import '@common/react/yupLocaleSettings';
import { LoaderProvider } from '@common/react/components/Core/LoaderProvider/LoaderProvider';
import VideoChatModal from '@common/react/components/UI/VideoChat/VideoChatModal';
import ErrorBoundaryWithSentry from '@common/react/components/UI/ErrorBoundaryWithSentry/ErrorBoundaryWithSentry';
import { RequestProvider } from '@common/react/components/RequestProvider/RequestProvider';
import VideoChatGlider from '@common/react/components/Chat/VideoChatGlider';
import Application from '@common/react/components/Core/Application/Application';
import NotificationGliderProvider from '@common/react/components/Chat/NotificationGliderProvider';
import ReactionNotificationHandler from '@common/react/components/Chat/ReactionNotificationHandler';
import { StickerProvider } from '@common/react/components/Chat/Sticker/StickerProvider';
import ChatModal from '@common/react/components/Chat/ModalPlugin/ChatModal';
import Avatar from '@common/react/components/Forms/Avatar';
import ChatNotificationHandler from '@common/react/components/Chat/ChatNotificationHandler';
import AntdModalContextProvider from '@common/react/components/Modal/AntdModalContextProvider';
import BrowserNotificationChecker from '@common/react/components/UI/BrowserNotificationChecker/BrowserNotificationChecker';
import IdleTimer from '@common/react/components/Core/IdleTimer/IdleTimer';
import { CommentsProvider } from '@common/react/components/Forms/Comment/CommentsProvider';
import EditableTableSettingsProvider from '@common/react/components/Core/EditableTable/EditableTableSettingsProvider';
import { AlertNotificationHandler } from '@common/react/components/Modal/AlertNotificationHandler';
import GoogleKeysProvider from '@common/react/components/google/GoogleKeysProvider/GoogleKeysProvider';
import TransmutationBackKeyboard from '@common/react/components/UI/Transmutation/TransmutationBackKeyboard';

import { LogoLoaderPage } from '@commonTuna/react/components/UI/Loaders/LogoLoader';
import { UserGroupType } from '@commonTuna/react/objects/UserGroup';

import CompanyFeaturesSetter from '@app/components/UI/CompanyFeaturesSetter/CompanyFeaturesSetter';
import CompanySettingsSetter from '@app/components/UI/CompanySettingsSetter/CompanySettingsSetter';
import { UserStatusNotificationHandler } from '@app/components/UI/UserAvatarWithStatus';
import PhoneCallModal from '@app/components/UI/PhoneCallPopupModal/PhoneCallModal';
import UserFavoritesProvider from '@app/components/UI/UserFavoritesProvider/UserFavoritesProvider';
import PhoneCallProvider from '@app/components/UI/PhoneCallProvider/PhoneCallProvider';
import MessageFlashingTime from '@app/components/UI/MessageFlashingTitle';
import { SupportChatSettingsProvider } from '@app/components/Pages/Admin/SupportChats/SupportChats';
import UserGroupProvider from '@app/components/UI/UserGroupProvider/UserGroupProvider';
import { useSickerCollections } from '@app/components/Layouts/DashboardLayout/stickerCollections';
import { SystemChatSettingsProvider } from '@app/components/Pages/Admin/Chats/SystemChatSettingsProvider';
import HiddenAppointmentDotsSetter from '@app/components/UI/HiddenAppointmentDotsSetter/HiddenAppointmentDotsSetter';
import { HandleNotificationReviewRequest }
	from '@app/components/Pages/Admin/Appointments/AppointmentForm/HandleNotificationReviewRequest';
import EnumAccessProvider from '@app/components/UI/EnumAccessProvider';
import CheckDevice from '@app/components/UI/CheckDevice/CheckDevice';
import { StatusesSynchronizer } from '@app/components/UI/AppointmentStatus/StatusesSynchronizer';
import { LocationsSynchronizer } from '@app/components/UI/Location/LocationsSynchronizer';
import { AppointmentTypesSynchronizer } from '@app/components/UI/AppointmentType/AppointmentTypesSynchronizer';
import { User, UserRole } from '@app/objects/User';
import { customReduxActions } from '@app/store/CustomReduxActions';
import { Init } from '@app/objects/Init';
import TranscriptionNotificationHandler
	from '@app/components/Pages/Admin/TranscriptionNotificationHandler/TranscriptionNotificationHandler';
import { StatusNotificationHandler } from '@app/components/UI/AppointmentStatus/StatusNotificationHandler';
import SearchModal from '@app/components/UI/SearchModal/SearchModal';
import UserResearchSignatureFormModal
	from '@app/components/Pages/Admin/Patients/PatientDisclosuresForm/UserResearchSignatureForm';
import keys from '@app/utils/keys/keys';

import '@app/scss/style.scss';

import imageSkeleton from '@images/no-image-available.jpg';

const renderAvatar = (state) => (
	<Avatar
		type=""
		object={{ avatar: `${state.avatar}` }}
		readonly
		className="avatar-container"
		showOpenIcon={false}
		showDownload={false}
		withModal={false}
		firstName={state.firstName ?? 'N'}
		lastName={state.lastName ?? 'N'}
	/>
);

const notFoundPageMessage = 'Ups… page not found or you don’t have access to it. Please contact your system administrator';

const LayoutInner: React.FC = ({ children }) => {
	const collections = useSickerCollections();

	return <>
		<AlertNotificationHandler />
		<StatusNotificationHandler />
		<BrowserNotificationChecker />
		<IdleTimer />
		<UserStatusNotificationHandler />
		<UserStatusNotificationHandler storeName="userPortalStatuses" />
		<CommentsProvider<User>>
			<CompanyFeaturesSetter>
				<CompanySettingsSetter>
					<HiddenAppointmentDotsSetter>
						<UserGroupProvider>
							{({ checkUserGroupAccess, toggleUserGroup }) => <EnumAccessProvider>
								<StickerProvider collections={collections} skeleton={imageSkeleton}>
									<StatusesSynchronizer />
									<LocationsSynchronizer />
									<AppointmentTypesSynchronizer />
									<NotificationGliderProvider>
										<HandleNotificationReviewRequest />
										<PhoneCallProvider>
											<SystemChatSettingsProvider>
												<UserFavoritesProvider>
													<EditableTableSettingsProvider bordered={false}>
														<SearchModal>
															{({ open }) => <>
																{open ? null : <TransmutationBackKeyboard />}
																{children || <Outlet />}
															</>}
														</SearchModal>
													</EditableTableSettingsProvider>
												</UserFavoritesProvider>
												<ChatModal
													initFullMode={checkUserGroupAccess(UserGroupType.ShowChatInModal)}
													onChangeFullMode={(value) => toggleUserGroup(UserGroupType.ShowChatInModal, value)}
												/>
												<ReactionNotificationHandler />
												<TranscriptionNotificationHandler />
												<ChatNotificationHandler />
												<UserResearchSignatureFormModal />
												<VideoChatModal
													avatarComponent={(avatar, userName) => {
														const [firstName, lastName] = (userName || '').split(' ');
														return renderAvatar({ avatar, firstName, lastName });
													}}
													skeletonImage={imageSkeleton}
												/>
											</SystemChatSettingsProvider>
											<PhoneCallModal />
										</PhoneCallProvider>
										<VideoChatGlider
											renderAvatar={renderAvatar}
											imageSkeleton={imageSkeleton}
										/>
										<MessageFlashingTime />
										<SupportChatSettingsProvider>
											<ChatNotificationHandler withRemoteId />
										</SupportChatSettingsProvider>
									</NotificationGliderProvider>
								</StickerProvider>
							</EnumAccessProvider>}
						</UserGroupProvider>
					</HiddenAppointmentDotsSetter>
				</CompanySettingsSetter>
			</CompanyFeaturesSetter>
		</CommentsProvider>
	</>;
};

const Layout: React.FC = ({ children }) => {
	return (
		<div className="app-container">
			<CheckDevice />
			<ErrorBoundaryWithSentry>
				<AntdModalContextProvider>
					<GoogleKeysProvider
						mapKey={keys.GOOGLE_PLACE_API_KEY}
						geocodeKey={keys.GEOCODER_API_KEY}
						analyticsId="G-D5Q0SK7ZE5"
					>
						<LoaderProvider
							loader={<LogoLoaderPage />}
							other={{
								chunkLoader: <LogoLoaderPage />,
							}}
						>
							<RequestProvider notFoundPageMessage={notFoundPageMessage}>
								<Application<User, Init>
									initCustomHandler={customReduxActions}
									checkUserAccess={(user: User, roles: Array<UserRole>, props?: any) =>
										user && ((!props?.notForRoot && user.root) || roles.includes(user.role))}
								>
									{({ getUser }) => {
										const user = getUser<User>();
										if (!user) return <UserGroupProvider>{children || <Outlet />}</UserGroupProvider>;

										return <LayoutInner>{children}</LayoutInner>;
									}}
								</Application>
							</RequestProvider>
						</LoaderProvider>
					</GoogleKeysProvider>
				</AntdModalContextProvider>
			</ErrorBoundaryWithSentry>
		</div>
	);
};

export default Layout;
